import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import api from '../services/api';
import { searchGlobal } from '../services/searchService';
import { getDMs } from '../services/dmService';
import { getProjects } from '../services/projectService';
import { orderProjectsForWorkNav } from '../utils/workNavigation';
import './Sidebar.css';

const RECENT_SPACE_KEY = 'comsapp.sidebar.recentSpaces';
const PINNED_PROJECT_KEY = 'comsapp.sidebar.pinnedProjects';
const SIDEBAR_WIDTH_KEY = 'comsapp.sidebar.width';
const SIDEBAR_WIDTH_MIN = 220;
const SIDEBAR_WIDTH_DEFAULT = 260;
const SIDEBAR_WIDTH_MAX = 380;

const spaceContextLabel = (space) => {
  if (space.businessId?.name) return space.businessId.name;
  if (space.governance?.dataOwnerName) return space.governance.dataOwnerName;
  if (space.type === 'project') return 'Project Space';
  if (space.type === 'committee') return 'Committee Space';
  if (space.type === 'mailbox') return 'Inbox Space';
  if (space.type === 'client') return 'Client Space';
  if (space.type === 'org' || space.type === 'dept') return 'Business Space';
  return 'Space';
};

export default function Sidebar({ isOpen, onToggle, onNavigate }) {
  const { user, logout } = useAuth();
  const { spaces, createSpace } = useSpaces();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNewSpace, setShowNewSpace] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === 'undefined') return SIDEBAR_WIDTH_DEFAULT;
    const saved = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (!Number.isFinite(saved)) return SIDEBAR_WIDTH_DEFAULT;
    return Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, saved));
  });
  const [isDesktopSidebar, setIsDesktopSidebar] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 1024;
  });
  const [showSpacesList, setShowSpacesList] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 640;
  });
  const [topNavCollapsed, setTopNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('comsapp.sidebar.topNavCollapsed') === '1';
  });
  const [topNavHeight, setTopNavHeight] = useState(() => {
    if (typeof window === 'undefined') return 280;
    const saved = Number(window.localStorage.getItem('comsapp.sidebar.topNavHeight'));
    if (!Number.isFinite(saved) || saved <= 0) return 280;
    return saved;
  });
  const [spacesCollapsed, setSpacesCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('comsapp.sidebar.spacesCollapsed') === '1';
  });
  const [showAllSpaces, setShowAllSpaces] = useState(false);
  const [recentSpaceIds, setRecentSpaceIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(RECENT_SPACE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [pinnedProjectIds, setPinnedProjectIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(PINNED_PROJECT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isCompactSpaces, setIsCompactSpaces] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 640;
  });
  const [newSpaceName, setNewSpaceName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [focusSearchAfterOpen, setFocusSearchAfterOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [projects, setProjects] = useState([]);
  const prevInboxUnreadRef = useRef(0);
  const prevDMUnreadRef = useRef(0);
  const baselineSetRef = useRef(false);
  const soundRef = useRef(null);
  const searchInputRef = useRef(null);
  const orderedSpaces = useMemo(() => {
    const byId = new Map(spaces.map((space) => [String(space._id), space]));
    const recent = recentSpaceIds.map((id) => byId.get(String(id))).filter(Boolean);
    const seen = new Set(recent.map((space) => String(space._id)));
    const remaining = spaces
      .filter((space) => !seen.has(String(space._id)))
      .sort((left, right) => new Date(right.updatedAt || 0) - new Date(left.updatedAt || 0));
    return [...recent, ...remaining];
  }, [recentSpaceIds, spaces]);
  const projectPrimarySpaceIds = useMemo(
    () => new Set(projects.map((project) => String(project.primarySpaceId?._id || project.primarySpaceId || '')).filter(Boolean)),
    [projects]
  );
  const recentNonProjectSpaces = orderedSpaces.filter((space) => !projectPrimarySpaceIds.has(String(space._id))).slice(0, 5);
  const visibleSpaces = showAllSpaces ? orderedSpaces : recentNonProjectSpaces;
  const orderedProjects = useMemo(() => orderProjectsForWorkNav(projects, pinnedProjectIds).slice(0, 7), [pinnedProjectIds, projects]);

  const formatUnread = (count) => (count > 99 ? '99+' : String(count));

  const playNotificationSound = () => {
    if (!soundRef.current) return;
    soundRef.current.currentTime = 0;
    soundRef.current.play().catch(() => {});
  };

  const notifyBrowser = (title, body) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      new Notification(title, { body });
    } catch {
      // Ignore browser notification failures and keep badge updates.
    }
  };

  useEffect(() => {
    soundRef.current = new Audio('/sound/Message.wav');
    soundRef.current.volume = 0.6;
    return () => {
      soundRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.topNavCollapsed', topNavCollapsed ? '1' : '0');
  }, [topNavCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.spacesCollapsed', spacesCollapsed ? '1' : '0');
  }, [spacesCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(RECENT_SPACE_KEY, JSON.stringify(recentSpaceIds.slice(0, 12)));
  }, [recentSpaceIds]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PINNED_PROJECT_KEY, JSON.stringify(pinnedProjectIds.slice(0, 20)));
  }, [pinnedProjectIds]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.topNavHeight', String(topNavHeight));
  }, [topNavHeight]);

  useEffect(() => {
    if (!isOpen || !focusSearchAfterOpen || !searchInputRef.current) return;
    searchInputRef.current.focus();
    setFocusSearchAfterOpen(false);
  }, [isOpen, focusSearchAfterOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError('');
      setSearchLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setSearchLoading(true);

    const timerId = window.setTimeout(() => {
      searchGlobal(query, 7)
        .then((res) => {
          if (isCancelled) return;
          setSearchResults(res.data.results || []);
          setSearchError('');
        })
        .catch((err) => {
          if (isCancelled) return;
          setSearchResults([]);
          setSearchError(err?.response?.data?.message || 'Search failed.');
        })
        .finally(() => {
          if (!isCancelled) setSearchLoading(false);
        });
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timerId);
    };
  }, [isOpen, searchQuery]);

  useEffect(() => {
    const clampTopNavHeight = (value) => {
      const min = 120;
      const max = Math.max(220, Math.floor(window.innerHeight * 0.58));
      return Math.max(min, Math.min(max, value));
    };

    const onResize = () => {
      const compact = window.innerWidth <= 640;
      setIsDesktopSidebar(window.innerWidth > 1024);
      setIsCompactSpaces(compact);
      if (!compact) {
        setShowSpacesList(true);
      }
      setTopNavHeight((value) => clampTopNavHeight(value));
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let active = true;
    getProjects()
      .then((res) => {
        if (!active) return;
        setProjects(res.data.projects || []);
      })
      .catch(() => {
        if (active) setProjects([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadUnreadCounts = async () => {
      try {
        const [inboxRes, dmsRes] = await Promise.all([
          api.get('/inbox/unread-count'),
          getDMs(),
        ]);

        const nextInboxUnread = Number(inboxRes?.data?.count || 0);
        const threads = Array.isArray(dmsRes?.data?.threads) ? dmsRes.data.threads : [];
        const nextDMUnread = threads.reduce((sum, thread) => sum + Number(thread?.unreadCount || 0), 0);

        if (!isMounted) return;

        setInboxUnread(nextInboxUnread);
        if (baselineSetRef.current) {
          let shouldPlaySound = false;

          if (nextInboxUnread > prevInboxUnreadRef.current && location.pathname !== '/inbox') {
            notifyBrowser('New Email', 'You have new unread inbox messages.');
            shouldPlaySound = true;
          }

          if (nextDMUnread > prevDMUnreadRef.current && location.pathname !== '/inbox') {
            notifyBrowser('New Message', 'You have new unread direct messages.');
            shouldPlaySound = true;
          }

          if (shouldPlaySound) {
            playNotificationSound();
          }
        }

        prevInboxUnreadRef.current = nextInboxUnread;
        prevDMUnreadRef.current = nextDMUnread;
        baselineSetRef.current = true;
      } catch {
        if (!isMounted) return;
        setInboxUnread((current) => current);
      }
    };

    loadUnreadCounts();
    const intervalId = setInterval(loadUnreadCounts, 12000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [location.pathname]);

  const handleCreateSpace = async (e) => {
    e.preventDefault();
    if (!newSpaceName.trim()) return;
    try {
      const space = await createSpace({ name: newSpaceName.trim() });
      setNewSpaceName('');
      setShowNewSpace(false);
      navigate(`/spaces/${space._id}`);
    } catch (err) {
      console.error('Failed to create space:', err);
      alert(err?.response?.data?.message || 'Failed to create space');
    }
  };

  const handleNavSelect = () => {
    if (typeof onNavigate === 'function') onNavigate();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleSearchSelect = (result) => {
    clearSearch();
    navigate(result.href || '/');
    handleNavSelect();
  };

  const recordSpaceVisit = (spaceId) => {
    setRecentSpaceIds((prev) => [String(spaceId), ...prev.filter((id) => String(id) !== String(spaceId))].slice(0, 12));
  };

  const toggleProjectPin = (projectId) => {
    setPinnedProjectIds((prev) => {
      const id = String(projectId);
      return prev.map(String).includes(id)
        ? prev.filter((entry) => String(entry) !== id)
        : [id, ...prev.map(String)].slice(0, 20);
    });
  };

  const clampTopNavHeight = (value) => {
    if (typeof window === 'undefined') return value;
    const min = 120;
    const max = Math.max(220, Math.floor(window.innerHeight * 0.58));
    return Math.max(min, Math.min(max, value));
  };

  const beginTopNavResize = (event) => {
    if (!isOpen || hideTopNav || spacesCollapsed) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    event.preventDefault();

    const startY = event.clientY;
    const startHeight = topNavHeight;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      setTopNavHeight(clampTopNavHeight(startHeight + delta));
    };

    const onUp = () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const beginSidebarResize = (event) => {
    if (!isOpen || !isDesktopSidebar) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = sidebarWidth;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setSidebarWidth(Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, startWidth + delta)));
    };

    const onUp = () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleSplitterKeyDown = (event) => {
    if (!isOpen || hideTopNav || spacesCollapsed) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    const delta = event.key === 'ArrowUp' ? -20 : 20;
    setTopNavHeight((value) => clampTopNavHeight(value + delta));
  };

  const hideTopNav = isOpen && topNavCollapsed;

  return (
    <aside
      className={`sidebar ${isOpen ? '' : 'collapsed'}`}
      style={isOpen && isDesktopSidebar ? { width: `${sidebarWidth}px` } : undefined}
    >
      <div className="sidebar-header">
        <span className="sidebar-logo">ComSapp</span>
        <button className="sidebar-toggle" onClick={onToggle} title="Toggle sidebar">
          {isOpen ? '‹' : '›'}
        </button>
      </div>

      <div
        className={`sidebar-top-nav ${hideTopNav ? 'collapsed' : ''} ${spacesCollapsed ? 'spaces-collapsed' : ''}`}
        style={isOpen && !hideTopNav && !spacesCollapsed ? { height: `${topNavHeight}px` } : undefined}
      >
        {isOpen && (
          <div className="sidebar-section-header sidebar-top-nav-header">
            <span>Menu</span>
            <button
              className="btn-xs-muted"
              type="button"
              onClick={() => setTopNavCollapsed((v) => !v)}
              aria-expanded={!hideTopNav}
              aria-controls="sidebar-top-nav-links"
              title={hideTopNav ? 'Expand top menu' : 'Collapse top menu'}
            >
              {hideTopNav ? 'Expand' : 'Collapse'}
            </button>
          </div>
        )}

        <nav id="sidebar-top-nav-links" className="sidebar-nav" onClick={handleNavSelect}>
          <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">⊞</span>
            {isOpen && <span>Home</span>}
          </NavLink>
          <NavLink to="/inbox" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon-wrap">
              <span className="nav-icon">✉</span>
              {!isOpen && inboxUnread > 0 && (
                <span className="nav-dot-badge" aria-label={`${inboxUnread} unread inbox messages`}>
                  {formatUnread(inboxUnread)}
                </span>
              )}
            </span>
            {isOpen && <span>Inbox</span>}
            {isOpen && inboxUnread > 0 && <span className="nav-count-badge">{formatUnread(inboxUnread)}</span>}
          </NavLink>
          <NavLink to="/calendar" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">◷</span>
            {isOpen && <span>Calendar</span>}
          </NavLink>
          <NavLink to="/tasks" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">✅</span>
            {isOpen && <span>Tasks</span>}
          </NavLink>
          <NavLink to="/files" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">⊘</span>
            {isOpen && <span>Files</span>}
          </NavLink>
          <NavLink to="/work" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">▦</span>
            {isOpen && <span>Work</span>}
          </NavLink>
          <NavLink to="/people" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">👥</span>
            {isOpen && <span>People</span>}
          </NavLink>
        </nav>
      </div>

      {isOpen && !hideTopNav && !spacesCollapsed && (
        <div className="sidebar-splitter-wrap">
          <div
            className="sidebar-splitter"
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize menu and spaces"
            tabIndex={0}
            onPointerDown={beginTopNavResize}
            onKeyDown={handleSplitterKeyDown}
          />
        </div>
      )}

      {isOpen && (
        <div className={`sidebar-section ${spacesCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-section-header">
            <span>{showAllSpaces ? 'All Spaces' : 'Pinned Work / Recent Work'}</span>
            <div className="sidebar-section-actions">
              {isCompactSpaces && (
                <button
                  className="btn-xs-muted"
                  onClick={() => setShowSpacesList((v) => !v)}
                  title={showSpacesList ? 'Hide spaces' : 'Show spaces'}
                >
                  {showSpacesList ? 'Hide' : `Show (${spaces.length})`}
                </button>
              )}
              <button
                className="btn-xs-muted"
                type="button"
                onClick={() => setShowAllSpaces((v) => !v)}
                title={showAllSpaces ? 'Show recent spaces' : 'Show all spaces'}
              >
                {showAllSpaces ? 'Recent' : 'All Spaces'}
              </button>
              <button
                className="btn-xs-muted"
                type="button"
                onClick={() => setSpacesCollapsed((v) => !v)}
                aria-expanded={!spacesCollapsed}
                aria-controls="sidebar-spaces-list"
                title={spacesCollapsed ? 'Expand spaces' : 'Collapse spaces'}
              >
                {spacesCollapsed ? 'Expand' : 'Collapse'}
              </button>
              {showAllSpaces && <button className="btn-icon" onClick={() => setShowNewSpace((v) => !v)} title="New space">+</button>}
            </div>
          </div>

          {!spacesCollapsed && showAllSpaces && showNewSpace && (
            <form onSubmit={handleCreateSpace} className="new-space-form">
              <input
                autoFocus
                value={newSpaceName}
                onChange={(e) => setNewSpaceName(e.target.value)}
                placeholder="Space name..."
                className="sidebar-input"
              />
              <button type="submit" className="btn-sm btn-primary">Create</button>
            </form>
          )}

          {!spacesCollapsed && (!isCompactSpaces || showSpacesList) && (
            <ul id="sidebar-spaces-list" className="space-list" onClick={handleNavSelect}>
              {!showAllSpaces && orderedProjects.map((project) => {
                const projectSpaceId = project.primarySpaceId?._id || project.primarySpaceId;
                const isPinned = pinnedProjectIds.map(String).includes(String(project._id));
                return (
                  <li key={project._id} className="work-nav-project-item">
                    <button
                      type="button"
                      className={`work-project-pin ${isPinned ? 'pinned' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleProjectPin(project._id);
                      }}
                      title={isPinned ? 'Unpin project' : 'Pin project'}
                      aria-label={isPinned ? `Unpin ${project.name}` : `Pin ${project.name}`}
                    >
                      {isPinned ? '★' : '☆'}
                    </button>
                    <NavLink to={`/projects/${project._id}`} className={({ isActive }) => isActive ? 'work-project-link active' : 'work-project-link'}>
                      <span className="space-badge work-badge">P</span>
                      <span className="space-text">
                        <span className="space-name">{project.name}</span>
                        <span className="space-context">{project.leadBusinessId?.name || 'Project'}</span>
                      </span>
                    </NavLink>
                    {projectSpaceId && (
                      <NavLink
                        to={`/spaces/${projectSpaceId}`}
                        className="work-project-space-link"
                        onClick={() => recordSpaceVisit(projectSpaceId)}
                        title="Open primary workroom"
                      >
                        Chat
                      </NavLink>
                    )}
                  </li>
                );
              })}
              {!showAllSpaces && recentNonProjectSpaces.length > 0 && (
                <li className="work-nav-subheading">Recent Conversations</li>
              )}
              {visibleSpaces.map((space) => (
                <li key={space._id}>
                  <NavLink
                    to={`/spaces/${space._id}`}
                    className={({ isActive }) => isActive ? 'space-item active' : 'space-item'}
                    onClick={() => recordSpaceVisit(space._id)}
                  >
                    <span className={`space-badge classification-${space.governance?.classification || 'internal'}`}>{space.name[0].toUpperCase()}</span>
                    <span className="space-text">
                      <span className="space-name">{space.name}</span>
                      <span className="space-context">{spaceContextLabel(space)}</span>
                    </span>
                  </NavLink>
                </li>
              ))}
              {!showAllSpaces && orderedSpaces.length > visibleSpaces.length && (
                <li>
                  <button type="button" className="space-all-button" onClick={() => setShowAllSpaces(true)}>
                    All Spaces ({orderedSpaces.length})
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {isOpen && isDesktopSidebar && (
        <div
          className="sidebar-width-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize navigation"
          onPointerDown={beginSidebarResize}
        />
      )}

      <div className="sidebar-footer">
        {isOpen ? (
          <div className="sidebar-search">
            <label className="sidebar-search-label" htmlFor="sidebar-search-input">Search</label>
            <div className="sidebar-search-row">
              <span className="sidebar-search-icon">⌕</span>
              <input
                id="sidebar-search-input"
                ref={searchInputRef}
                className="sidebar-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search spaces, files, people..."
                aria-label="Search everything"
              />
              {searchQuery && (
                <button type="button" className="sidebar-search-clear" onClick={clearSearch} aria-label="Clear search">
                  ×
                </button>
              )}
            </div>
            {searchLoading && <p className="sidebar-search-status">Searching...</p>}
            {searchError && <p className="sidebar-search-status sidebar-search-error">{searchError}</p>}
            {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length > 0 && (
              <ul className="sidebar-search-results" aria-label="Search results">
                {searchResults.map((result) => (
                  <li key={`${result.type}-${result.meta?.id || result.title}`}>
                    <button type="button" className="sidebar-search-result" onClick={() => handleSearchSelect(result)}>
                      <span className={`sidebar-search-pill type-${result.type}`}>{result.type}</span>
                      <span className="sidebar-search-result-text">
                        <strong>{result.title}</strong>
                        <span>{result.subtitle}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchError && (
              <p className="sidebar-search-status">No results found.</p>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="sidebar-search-collapsed"
            onClick={() => {
              setFocusSearchAfterOpen(true);
              onToggle?.();
            }}
            title="Open search"
            aria-label="Open search"
          >
            ⌕
          </button>
        )}
        {user?.role === 'admin' && (
          <NavLink
            to="/admin"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
            style={{ margin: '0 0 6px 0' }}
            onClick={handleNavSelect}
          >
            <span className="nav-icon">⚙</span>
            {isOpen && <span>Admin</span>}
          </NavLink>
        )}
        <NavLink
          to="/account"
          className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          style={{ margin: '0 0 6px 0' }}
          onClick={handleNavSelect}
        >
          <span className="nav-icon">👤</span>
          {isOpen && <span>Account</span>}
        </NavLink>
        <div className="user-info">
          <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
          {isOpen && (
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <button className="btn-logout" onClick={logout}>Sign out</button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
